import { useState } from 'react';
import { errorText } from '../lib/errors';
import { alertsSnoozed, disablePush, enablePush, snoozeAlerts, testPush, useInstallPrompt, usePush } from '../lib/push';
import { useToast } from '../lib/toast';
import { ErrorNote } from './ui';

const host = () => location.host;

const ShareIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12M8 7l4-4 4 4" /><path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" />
  </svg>
);
const AddIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <rect x="4" y="4" width="16" height="16" rx="4" /><path d="M12 8v8M8 12h8" />
  </svg>
);

/** Add to Home Screen on an iPhone: the only way iOS lets a website send notifications. */
export function IosSteps() {
  return (
    <>
      <ol className="isteps">
        <li><span className="isteps-n"><ShareIcon /></span><span>Tap <b>Share</b> in Safari's toolbar.</span></li>
        <li><span className="isteps-n"><AddIcon /></span><span>Choose <b>Add to Home Screen</b>, then <b>Add</b>.</span></li>
        <li><span className="isteps-n">3</span><span>Open <b>Food Pass</b> from your Home Screen, sign in with your email code and tap <b>Turn on alerts</b>.</span></li>
      </ol>
      <p className="fine">No "Add to Home Screen"? Open {host()} in Safari first: pages opened inside WhatsApp or Gmail can't be added. Needs iOS 16.4 or newer.</p>
    </>
  );
}

function useEnable() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function enable() {
    setBusy(true);
    setError(null);
    try {
      const r = await enablePush();
      if (r === 'on') toast('Alerts are on for this phone');
      else if (r === 'denied') setError(`Notifications are blocked. Allow them for ${host()} in the browser's site settings.`);
      else if (r === 'unavailable') setError("Couldn't reach the phone's notification service. Check the connection, reload and try again.");
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  return { enable, busy, error };
}

/** On My passes until this phone has alerts on (or "Not now" for two days). */
export function AlertsCard() {
  const p = usePush();
  const { canInstall, install } = useInstallPrompt();
  const { enable, busy, error } = useEnable();
  if (p.on !== false || alertsSnoozed() || p.support === 'off' || p.support === 'unsupported') return null;
  const notNow = <button type="button" className="btn quiet sm" onClick={() => snoozeAlerts(2)}>Not now</button>;

  if (p.support === 'ios-install') {
    return (
      <section className="alerts">
        <p className="alerts-eyebrow">Meal alerts on iPhone</p>
        <p className="alerts-title">Add Food Pass to your Home Screen</p>
        <p className="alerts-text">Then we can tell you when your meal is being served, which hall to go to, and before it closes.</p>
        <IosSteps />
        <div className="row">{notNow}</div>
      </section>
    );
  }
  if (p.permission === 'denied') {
    return (
      <section className="alerts">
        <p className="alerts-eyebrow">Meal alerts</p>
        <p className="alerts-text">Notifications are blocked for {host()}. To get meal alerts, allow them in your browser's site settings, then reload.</p>
        <div className="row">{notNow}</div>
      </section>
    );
  }
  return (
    <section className="alerts">
      <p className="alerts-eyebrow">Meal alerts</p>
      <p className="alerts-title">Know when your meal is being served</p>
      <p className="alerts-text">We'll tell you which hall to go to when your food is served, remind you before it closes, and pass on Food Committee notices.</p>
      <ErrorNote error={error} />
      <div className="row">
        <button type="button" className="btn primary sm" disabled={busy} onClick={() => void enable()}>{busy ? 'Turning on…' : 'Turn on alerts'}</button>
        {canInstall && <button type="button" className="btn ghost sm" onClick={() => void install()}>Install the app</button>}
        {notNow}
      </div>
    </section>
  );
}

/** "Alerts on this phone" in My family. */
export function AlertsSetting() {
  const p = usePush();
  const toast = useToast();
  const { canInstall, install } = useInstallPrompt();
  const { enable, busy, error } = useEnable();
  const [testing, setTesting] = useState(false);
  if (p.support === 'off') return null;

  async function test() {
    setTesting(true);
    try {
      const n = await testPush();
      toast(n ? 'Test alert sent. It should arrive in a few seconds.' : 'A test was sent a moment ago. Try again in a minute.');
    } catch (e) {
      toast(errorText(e), { tone: 'bad' });
    } finally {
      setTesting(false);
    }
  }

  return (
    <section className="card stack">
      <div className="setting" style={{ padding: 0, border: 0 }}>
        <div>
          <b>Alerts on this phone</b>
          <p>When your meal is being served and where, last call before it closes, payment updates and Food Committee notices.</p>
        </div>
        {p.support === 'ok' && p.permission !== 'denied' && (
          <button className="switch" role="switch" aria-checked={!!p.on} aria-label="Alerts on this phone" disabled={busy || p.on === null}
            onClick={() => void (p.on ? disablePush() : enable())} />
        )}
      </div>
      {p.support === 'ios-install' && <><p className="fine">On iPhone, add Food Pass to your Home Screen first, then turn alerts on from there.</p><IosSteps /></>}
      {p.support === 'unsupported' && <p className="fine">This browser can't show alerts. Use Chrome on Android, or Safari on iPhone from the Home Screen.</p>}
      {p.support === 'ok' && p.permission === 'denied' && <p className="fine">Notifications are blocked for {host()}. Allow them in the browser's site settings, then reload.</p>}
      <ErrorNote error={error} />
      <div className="row">
        {p.on && <button className="btn ghost sm" disabled={testing} onClick={() => void test()}>{testing ? 'Sending…' : 'Send a test alert'}</button>}
        {canInstall && <button className="btn ghost sm" onClick={() => void install()}>Install the app</button>}
      </div>
    </section>
  );
}
